import React, { useContext, useEffect, useMemo, useState } from 'react';
import { connect, DispatchProp, useSelector } from 'react-redux';
import { CCol, CRow, CAlert, CContainer } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import * as pluralize from 'pluralize';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { RootState } from '../../store/state';
import { Job, LIVE_MAP_PHASES } from '../../shared/models/jobs';
import JobList from '../components/JobList';
import { StartJobsPoll, StopJobsPoll } from '../../jobs/actions';
import { selectActiveJobs, selectJobs, selectNotActiveJobs } from '../../store/selectors/job.selectors';
import { selectTechniciansIncludingRemoved } from '../../store/selectors/user.selectors';
import { UserInfo } from '../../shared/models/user';
import { LoadUsers } from '../../technicians/actions/userManagement.action';
import ActiveJobsMap from '../components/ActiveJobsMap';
import { ConfigContext } from '../../shared/contexts/journeyConfig.context';
import JobDetailsModal from '../components/JobDetailsModal';
import CompletedJobList from '../components/CompletedJobList';
import OrgHierarchyFilter from '../../shared/components/orgHierarchy/OrgHierarchyFilter';
import { selectOrgHierarchySelectedOrg, selectOrgList } from '../../store/selectors/orgHierarchy.selectors';
import { selectJourneyConfig } from '../../store/selectors/journey.selectors';
import orgLocationIcon from '../../assets/images/logo-org-new.png';
import { OrganizationConfig } from '../../shared/models/organization';
import { StartSessionsPoll, StopSessionsPoll } from '../../jobs/actions/sessions';
import { 
  selectActiveJobsFromSessions, 
  selectNotActiveJobsFromSession,
  selectJobsFromSessions
} from '../../store/selectors/session.selectors';

export const MAX_QUICKSTART_TECHNICIANS = 2;

export interface DashboardViewPageProps extends DispatchProp {
  jobs: Job[];
  activeJobs: Job[];
  notActiveJobs: Job[];
  jobsLoading: boolean;
  technicians: UserInfo[];
  techniciansLoading: boolean;
  baseLocation: { lat?: number, lng?: number };
  orgConfig: OrganizationConfig;
  activeJobsFromSessions: Job[];
  notActiveJobsFromSessions: Job[];
  allJobsFromSessions: Job[];
}

const DashboardViewPage = (props: DashboardViewPageProps) => {
  const { 
    jobs, 
    activeJobs, 
    notActiveJobs, 
    jobsLoading, 
    technicians, 
    techniciansLoading, 
    dispatch, 
    baseLocation,
    orgConfig,
    activeJobsFromSessions,
    notActiveJobsFromSessions,
    allJobsFromSessions,
  } = props;
  const config = useSelector(selectJourneyConfig);
  const [showQuickstart, setShowQuickstart] = useState(localStorage.getItem('show-quickstart') === null);
  const [selectedJob, setSelectedJob] = useState(null);
  const {
    enableOrgHierarchyModule
  } = useFlags();
  const labels = useContext(ConfigContext);

  const selectedOrg = useSelector(selectOrgHierarchySelectedOrg);
  const selectedOrgID = selectedOrg?._id;
  const orgs = useSelector(selectOrgList);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const isSessionsEnabled = orgConfig?.sessions?.enabled;

  useEffect(() => {
    if (technicians?.length > MAX_QUICKSTART_TECHNICIANS) {
      setShowQuickstart(false);
    }
  }, [technicians])

  useEffect(() => {
    if (selectedOrgID) {
      setSelectedOrgs([]);
      if (isSessionsEnabled) {
        dispatch(new StartSessionsPoll(selectedOrgID, {
          from_ts: moment().startOf('day').subtract(3, 'days').unix()*1000,
          to_ts: moment().endOf('day').unix()*1000,
          children: enableOrgHierarchyModule,
        }));
      } else {
        dispatch(new StartJobsPoll(selectedOrgID, {
          from_ts: moment().startOf('day').subtract(3, 'days').unix()*1000,
          to_ts: moment().endOf('day').unix()*1000,
          children: enableOrgHierarchyModule,
        }));
      }
  
      dispatch(new LoadUsers({
        orgId: selectedOrgID,
        includeChildren: enableOrgHierarchyModule
      }));
    }
    return () => {
      dispatch(new StopSessionsPoll());
      dispatch(new StopJobsPoll());
    }
  }, [selectedOrgID, isSessionsEnabled]);


  const handleShowChange = show => {
    if (!show) {
      setShowQuickstart(false);
      localStorage.setItem('show-quickstart', 'false');
    }
  };

  const selectJobHandler = (id) => {
    const jobsToSearch = isSessionsEnabled ? allJobsFromSessions : jobs;
    if (selectedJob?.invite_code !== id) {
      const job = jobsToSearch?.find(item => item.invite_code === id);
      setSelectedJob(job);
    } else {
      setSelectedJob(null);
    }
  };
  
  const selectedOrgIds = useMemo(() => {
    return selectedOrgs.map(org=>org.value);
  }, [selectedOrgs])

  const allActiveJobs = useMemo(() => {
    const result = isSessionsEnabled ? activeJobsFromSessions : activeJobs; 
    return result?.filter(job => selectedOrgIds?.length > 0 ? selectedOrgIds?.indexOf(job.org_id) > -1 : true);
  }, [isSessionsEnabled, activeJobs, activeJobsFromSessions, selectedOrgIds]);

  const liveMapJobs = useMemo(() => {
    return allActiveJobs?.filter((job) => job?.phase && LIVE_MAP_PHASES.includes(job.phase)) ?? [];
  }, [allActiveJobs]);

  const allNotActiveJobs = useMemo(() => {
    let result;
    if (isSessionsEnabled) {
      result = notActiveJobsFromSessions
        ?.sort((a, b) => {
          if (a?.session_id === b?.session_id) {
            return a?.rank > b?.rank ? -1 : 1;
          }
          return a?.session_id > b?.session_id ? -1 : 1;
        });
    } else {
      result = notActiveJobs;
    }
    return result?.filter(job => selectedOrgIds?.length > 0 ? selectedOrgIds?.indexOf(job.org_id) > -1 : true);
  }, [isSessionsEnabled, activeJobs, notActiveJobsFromSessions, selectedOrgs]);

  return (
    <>
      <JobDetailsModal job={selectedJob} isOpen={!!selectedJob} onToggle={() => {setSelectedJob(null)}} />
      <CContainer fluid>
        <CRow>
          <CCol md="12">
            <CAlert
              className='d-block text-center'
              color='warning'
              closeButton
              show={ showQuickstart }
              onShowChange={ handleShowChange }
            >
              <CIcon name="cil-book" className="mfe-2"/>
              Download our <a rel='noreferrer' target='_blank' className='quick-start-link' href='https://glympse.com/wp-content/uploads/2021/11/Glympse-Pro-Quick-Start-Guide.pdf'>Quick Start Guide</a>
            </CAlert>
          </CCol>
        </CRow>
        {
          selectedOrgID && (
            <CRow className="mb-4">
              <CCol md="12">
                <ActiveJobsMap
                  key={selectedOrgID}
                  config={baseLocation ? {
                    area: {
                      centerLat: baseLocation?.lat || 0,
                      centerLng: baseLocation?.lng || 0,
                      allowMarkers: true,
                      markers: [{
                        lat: baseLocation?.lat,
                        lng: baseLocation?.lng,
                        ext: {
                          w: config?.theme?.orgIcon?.w || 56,
                          h: config?.theme?.orgIcon?.h || 56,
                          path: config?.theme?.orgIcon?.url || orgLocationIcon,
                        }
                      }]
                    }
                  } : {}}
                  activeTasks={liveMapJobs}
                  onSelectJob={selectJobHandler}
                />
              </CCol>
            </CRow>
          )
        }
        <CRow className="justify-content-end">
        {
          enableOrgHierarchyModule ? (
            <div style={{display: 'inline-block', textAlign: 'left'}}>
              <OrgHierarchyFilter
                handleChange={(items) => {
                  setSelectedOrgs(items)
                }}
                options={orgs?.reduce((acc, item) => {
                  if (item.hierarchy?.indexOf(selectedOrgID) > -1 || item._id === selectedOrgID) {
                    acc.push({
                      label: item.name,
                      value: item._id
                    });
                  }
                  return acc;
                }, []) ?? []}
                selectedOrgs={selectedOrgs}/>
            </div>
          ) : null
        }
        </CRow>
        <CRow>
          <CCol md="6">
            <JobList
              title={ `Today's Active ${ pluralize(labels.task) }` }
              jobs={ allActiveJobs }
              technicians={ technicians }
              loading={ jobsLoading || techniciansLoading }
              isSessionsEnabled={isSessionsEnabled}
            />
          </CCol>
          <CCol md="6">
            <CompletedJobList
              title={ `Today's Completed / Canceled ${ pluralize(labels.task) }` }
              jobs={ allNotActiveJobs }
              technicians={ technicians }
              loading={ jobsLoading || techniciansLoading }
              isSessionsEnabled={isSessionsEnabled}
            />
          </CCol>
        </CRow>
      </CContainer>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  jobs: selectJobs(state),
  activeJobs: selectActiveJobs(state),
  notActiveJobs: selectNotActiveJobs(state),
  jobsLoading: state?.jobs?.jobs?.isLoading,
  technicians: selectTechniciansIncludingRemoved(state),
  techniciansLoading: state?.userManagement?.users?.isLoading,
  baseLocation: state?.orgHierarchy?.selectedOrg?.base_location,
  orgConfig: state?.orgHierarchy?.selectedOrg?.config,
  activeJobsFromSessions: selectActiveJobsFromSessions(state),
  notActiveJobsFromSessions: selectNotActiveJobsFromSession(state),
  allJobsFromSessions: selectJobsFromSessions(state),
});

export default connect(mapStateToProps)(DashboardViewPage);
